import { canUseActiveFieldGrant } from './permissionPolicy';
import type {
  CpqState,
  CustomFieldOwner,
  CustomFieldValue,
  Quote,
  QuoteFieldDefinitionRecord,
} from './types';

export type QuoteLineWriteAuthorization = (
  quote: Quote,
  field: QuoteFieldDefinitionRecord,
) => boolean;

function canWriteFormalQuoteLine(
  quote: Quote,
  field: QuoteFieldDefinitionRecord,
  authorize: QuoteLineWriteAuthorization,
) {
  return (
    quote.status === 'draft' &&
    field.enabled &&
    field.sourceKind === 'manual' &&
    field.governance.kind === 'custom' &&
    field.governance.classification === 'ordinary' &&
    !isCalculatedCustomField(field) &&
    authorize(quote, field)
  );
}

export const ENTERPRISE_CUSTOM_FIELD_TYPES = [
  'text',
  'number',
  'money',
  'percent',
  'date',
  'single-select',
  'multi-select',
  'boolean',
] as const;

/** The field-center tabs are the four catalog owners. Quote-line fields are
 * intentionally created only from the quote field-pool entry point; quote
 * header has fixed system fields and no enterprise custom tab. */
export const FIELD_CENTER_CUSTOM_FIELD_OWNERS = [
  'device',
  'package',
  'plan',
  'customer',
] as const;

export const QUOTE_LINE_CUSTOM_FIELD_OWNER = 'quote-line' as const;

export function isFieldCenterCustomOwner(
  owner: CustomFieldOwner,
): owner is (typeof FIELD_CENTER_CUSTOM_FIELD_OWNERS)[number] {
  return (FIELD_CENTER_CUSTOM_FIELD_OWNERS as readonly string[]).includes(
    owner,
  );
}

export function ownerForCustomDefinition(
  field: QuoteFieldDefinitionRecord,
): CustomFieldOwner | null {
  if (field.governance.kind !== 'custom' || field.sourceKind !== 'manual')
    return null;
  const owner = field.groupKey.replace(/^business:/, '');
  return ['device', 'package', 'plan', 'customer', 'quote-line'].includes(owner)
    ? (owner as CustomFieldOwner)
    : null;
}

export function isCalculatedCustomField(field: QuoteFieldDefinitionRecord) {
  return field.governance.kind === 'custom' && Boolean(field.calculation);
}

export function isValidCustomFieldDefinition(
  field: QuoteFieldDefinitionRecord,
): boolean {
  if (
    !ownerForCustomDefinition(field) ||
    !ENTERPRISE_CUSTOM_FIELD_TYPES.includes(field.type as never)
  )
    return false;
  if (field.calculation) {
    return (
      field.type === field.calculation.resultType &&
      field.calculation.owner === ownerForCustomDefinition(field) &&
      field.options === undefined
    );
  }
  const options = field.options;
  if (field.type === 'single-select' || field.type === 'multi-select') {
    return (
      Array.isArray(options) &&
      options.length > 0 &&
      options.every(
        (option) =>
          typeof option.value === 'string' &&
          option.value.trim().length > 0 &&
          typeof option.label === 'string' &&
          option.label.trim().length > 0,
      ) &&
      new Set(options.map((option) => option.value)).size === options.length
    );
  }
  return (
    options === undefined || (Array.isArray(options) && options.length === 0)
  );
}

export function isValidCustomFieldValue(
  field: QuoteFieldDefinitionRecord,
  value: unknown,
): value is CustomFieldValue {
  if (!isValidCustomFieldDefinition(field)) return false;
  if (field.type === 'text') return typeof value === 'string';
  if (field.type === 'date') {
    if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value))
      return false;
    const [year, month, day] = value.split('-').map(Number);
    const calendar = new Date(Date.UTC(year, month - 1, day));
    return (
      calendar.getUTCFullYear() === year &&
      calendar.getUTCMonth() === month - 1 &&
      calendar.getUTCDate() === day
    );
  }
  if (field.type === 'number')
    return typeof value === 'number' && Number.isFinite(value);
  if (field.type === 'money')
    return typeof value === 'number' && Number.isFinite(value) && value >= 0;
  if (field.type === 'percent')
    return (
      typeof value === 'number' &&
      Number.isFinite(value) &&
      value >= 0 &&
      value <= 100
    );
  if (field.type === 'boolean') return typeof value === 'boolean';
  const allowed = new Set(field.options?.map((option) => option.value));
  if (field.type === 'single-select')
    return typeof value === 'string' && allowed.has(value);
  return (
    Array.isArray(value) &&
    value.every((item) => typeof item === 'string' && allowed.has(item)) &&
    new Set(value).size === value.length
  );
}

/** Empty controls remove a value; they are not persisted as an empty string,
 * empty array or false sentinel. This keeps clear semantics identical across
 * all eight input renderers and preserves disabled historical values. */
export function normalizeCustomFieldClear(
  value: CustomFieldValue | null | undefined,
): CustomFieldValue | undefined {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'string' && value.length === 0) return undefined;
  if (Array.isArray(value) && value.length === 0) return undefined;
  return value;
}

export function customFieldsForOwner(
  definitions: QuoteFieldDefinitionRecord[],
  owner: CustomFieldOwner,
) {
  return definitions.filter(
    (field) => ownerForCustomDefinition(field) === owner,
  );
}

export function canViewCustomField(
  state: CpqState,
  field: QuoteFieldDefinitionRecord,
) {
  return (
    field.enabled &&
    field.governance.classification !== 'unclassified' &&
    canUseActiveFieldGrant(state, `definition:${field.key}`, 'view')
  );
}

export function canEditCustomField(
  state: CpqState,
  field: QuoteFieldDefinitionRecord,
) {
  return (
    canViewCustomField(state, field) &&
    !isCalculatedCustomField(field) &&
    canUseActiveFieldGrant(state, `definition:${field.key}`, 'edit')
  );
}

export function patchCustomFieldDraft(
  values: Record<string, CustomFieldValue> | undefined,
  key: string,
  value: CustomFieldValue | undefined,
) {
  const next = { ...values };
  if (value === undefined) delete next[key];
  else next[key] = value;
  return next;
}

export function updateCustomFieldValue(
  state: CpqState,
  owner: CustomFieldOwner,
  entityId: string,
  key: string,
  value: CustomFieldValue | undefined,
  quoteId?: string,
  quoteVersion?: number,
  authorizeQuoteLine?: QuoteLineWriteAuthorization,
): CpqState {
  if (owner === 'quote-header') throw new Error('报价抬头不开放企业自定义字段');
  const normalizedValue = normalizeCustomFieldClear(value);
  const field = state.catalog.quoteFieldDefinitions.find(
    (item) => item.key === key,
  );
  if (
    !field ||
    ownerForCustomDefinition(field) !== owner ||
    (normalizedValue !== undefined &&
      !isValidCustomFieldValue(field, normalizedValue))
  ) {
    throw new Error('企业字段值或归属无效');
  }
  if (
    (owner === 'device' &&
      !state.catalog.devices.some((item) => item.id === entityId)) ||
    (owner === 'package' &&
      !state.catalog.packages.some((item) => item.id === entityId)) ||
    (owner === 'plan' &&
      !state.catalog.plans.some((item) => item.id === entityId)) ||
    (owner === 'customer' &&
      !state.catalog.customers.some((item) => item.id === entityId))
  ) {
    throw new Error('企业字段对象不存在');
  }
  if (owner === 'quote-line') {
    if (!quoteId || quoteVersion === undefined)
      throw new Error('报价行必须绑定明确报价版本');
    const activeQuote = state.quotes[quoteId];
    if (!activeQuote) throw new Error('报价不存在');
    if (activeQuote.version !== quoteVersion)
      throw new Error('报价行版本不匹配');
    if (!activeQuote.items.some((item) => item.itemId === entityId))
      throw new Error('报价行不存在');
  }
  const mayWrite =
    owner === 'quote-line' && authorizeQuoteLine
      ? Boolean(
          quoteId &&
            state.quotes[quoteId] &&
            canWriteFormalQuoteLine(
              state.quotes[quoteId],
              field,
              authorizeQuoteLine,
            ),
        )
      : canEditCustomField(state, field);
  if (!mayWrite) throw new Error('当前角色无企业字段编辑权限');
  const apply = <
    T extends {
      id: string;
      customFieldValues?: Record<string, CustomFieldValue>;
    },
  >(
    records: T[],
  ) => {
    let found = false;
    const next = records.map((record) => {
      if (record.id !== entityId) return record;
      found = true;
      const customFieldValues = { ...record.customFieldValues };
      if (normalizedValue === undefined) delete customFieldValues[key];
      else customFieldValues[key] = normalizedValue;
      return { ...record, customFieldValues };
    });
    if (!found) throw new Error('企业字段对象不存在');
    return next;
  };
  if (owner === 'device')
    return {
      ...state,
      catalog: { ...state.catalog, devices: apply(state.catalog.devices) },
    };
  if (owner === 'package')
    return {
      ...state,
      catalog: { ...state.catalog, packages: apply(state.catalog.packages) },
    };
  if (owner === 'plan')
    return {
      ...state,
      catalog: { ...state.catalog, plans: apply(state.catalog.plans) },
    };
  if (owner === 'customer')
    return {
      ...state,
      catalog: { ...state.catalog, customers: apply(state.catalog.customers) },
    };
  const quote = quoteId ? state.quotes[quoteId] : undefined;
  if (!quote) throw new Error('报价不存在');
  return {
    ...state,
    quotes: {
      ...state.quotes,
      [quote.quoteId]: {
        ...quote,
        items: quote.items.map((item) => {
          if (item.itemId !== entityId) return item;
          const manualFieldValues = { ...item.manualFieldValues };
          if (normalizedValue === undefined) delete manualFieldValues[key];
          else manualFieldValues[key] = normalizedValue;
          return { ...item, manualFieldValues };
        }),
      },
    },
  };
}

export function assertCustomFieldDraft(
  state: CpqState,
  owner: CustomFieldOwner,
  current: Record<string, CustomFieldValue> | undefined,
  next: Record<string, CustomFieldValue> | undefined,
  quoteLineAuthorization?: {
    quote: Quote | undefined;
    authorize: QuoteLineWriteAuthorization;
  },
): void {
  const definitions = new Map(
    customFieldsForOwner(state.catalog.quoteFieldDefinitions, owner).map(
      (field) => [field.key, field],
    ),
  );
  const currentValues = current ?? {};
  const nextValues = next ?? {};
  for (const [key, value] of Object.entries(nextValues)) {
    const field = definitions.get(key);
    if (!field || !isValidCustomFieldValue(field, value))
      throw new Error('企业字段草稿包含无效归属或值');
  }
  const changedKeys = new Set([
    ...Object.keys(currentValues),
    ...Object.keys(nextValues),
  ]);
  for (const key of changedKeys) {
    if (JSON.stringify(currentValues[key]) === JSON.stringify(nextValues[key]))
      continue;
    const field = definitions.get(key);
    const mayWrite =
      field && owner === 'quote-line' && quoteLineAuthorization
        ? Boolean(
            quoteLineAuthorization.quote &&
              canWriteFormalQuoteLine(
                quoteLineAuthorization.quote,
                field,
                quoteLineAuthorization.authorize,
              ),
          )
        : field && canEditCustomField(state, field);
    if (!mayWrite) throw new Error('当前角色无企业字段编辑权限');
  }
}

export function replaceCustomFieldValues(
  _state: CpqState,
  owner: CustomFieldOwner,
  _entityId: string,
  _nextValues: Record<string, CustomFieldValue> | undefined,
): CpqState {
  if (owner === 'quote-header') throw new Error('报价抬头不开放企业自定义字段');
  throw new Error('该对象必须通过父对象保存命令提交企业字段草稿');
}

/** Repository boundary validation; values may exist on disabled definitions but
 * must keep their original type so historical facts cannot be corrupted. */
export function validateCustomFieldValuesInState(state: CpqState): boolean {
  const byKey = new Map(
    state.catalog.quoteFieldDefinitions.map((field) => [field.key, field]),
  );
  const valid = (owner: CustomFieldOwner, values: unknown) =>
    values === undefined ||
    (values !== null &&
      typeof values === 'object' &&
      Object.entries(values as Record<string, unknown>).every(
        ([key, value]) => {
          const field = byKey.get(key);
          // Legacy unbound raw manual facts are retained only for migration/audit.
          // They have no definition, cannot be selected and are never projected.
          const fieldOwner = field ? ownerForCustomDefinition(field) : null;
          if (!fieldOwner) return true;
          return Boolean(
            field &&
              fieldOwner === owner &&
              isValidCustomFieldValue(field, value),
          );
        },
      ));
  const validSourceSnapshot = (
    allowedOwners: readonly ('device' | 'package' | 'plan' | 'customer')[],
    values: unknown,
  ) =>
    values === undefined ||
    (values !== null &&
      typeof values === 'object' &&
      Object.entries(values as Record<string, unknown>).every(
        ([key, value]) => {
          const field = byKey.get(key);
          const fieldOwner = field ? ownerForCustomDefinition(field) : null;
          return (
            field?.governance.kind === 'custom' &&
            fieldOwner !== null &&
            allowedOwners.includes(
              fieldOwner as (typeof allowedOwners)[number],
            ) &&
            (value === null || isValidCustomFieldValue(field, value))
          );
        },
      ));
  return (
    state.catalog.devices.every((item) =>
      valid('device', item.customFieldValues),
    ) &&
    state.catalog.packages.every((item) =>
      valid('package', item.customFieldValues),
    ) &&
    state.catalog.plans.every((item) =>
      valid('plan', item.customFieldValues),
    ) &&
    state.catalog.customers.every((item) =>
      valid('customer', item.customFieldValues),
    ) &&
    Object.values(state.quotes).every(
      (quote) =>
        valid('quote-header', quote.customFieldValues) &&
        validSourceSnapshot(['customer'], quote.sourceFieldValueSnapshot) &&
        quote.items.every(
          (item) =>
            valid('quote-line', item.manualFieldValues) &&
            validSourceSnapshot(
              ['device', 'package', 'plan'],
              item.sourceFieldValueSnapshot,
            ),
        ),
    )
  );
}
